/* NOCRY global behaviour: header (mega panels, hide-on-scroll on phones), menu drawer with sub-levels, language switch,
   sticky offsets, card thumbnails, guide toggles, hash openers, the trial label test, support links, editor re-init. */
import { $, $$, layers, debounce, setRootVar, onReady, focusInto, keyboardUser, track, scrollMode } from '@theme/utils';
import { dispatchView } from '@theme/events';

function initNav() {
  /* the drawer renders inside the header section; that section's wrapper is sticky (its own stacking context),
     so the drawer moves to <body> to stack above the page overlay */
  const nav = $('[data-layer="nav"]');
  if (nav && nav.parentElement !== document.body) {
    $$('body > [data-layer="nav"]').forEach((old) => { if (old !== nav) old.remove(); });
    document.body.appendChild(nav);
  }
  $$('[data-nav]').forEach((el) => {
    if (el.__nav) return; el.__nav = true;
    el.addEventListener('click', (e) => { e.preventDefault(); layers.toggle('nav', $('[data-layer="nav"]')); });
  });
  const drawer = $('[data-layer="nav"]');
  if (drawer && !drawer.__nav) {
    drawer.__nav = true;
    drawer.addEventListener('click', (e) => { if (e.target.closest('a[href]')) layers.close('nav'); });
  }
  initMega(); initDrawerSubs();
}

/* desktop mega panels. Two ways in, so every pointer works:
   - click: the chevron toggles the panel; where hover can't open it (touch screens: iPads report hover:none even
     with a trackpad attached; or the header's "open on hover" setting is off) the category name toggles it too,
     and the panel's first row is the category link. A clicked-open panel stays until a click outside, Escape,
     another panel or focus leaving it.
   - hover (mouse or laptop trackpad, when the setting is on): opens after a short pause, closes a moment after the
     pointer leaves unless a click pinned it. Clicking the chevron on a hover-opened panel pins it rather than
     closing it. */
function initMega() {
  const nav = $('.hdr .nav'); if (!nav || nav.__mega) return;
  const items = $$('.ni', nav); if (!items.length) return;
  nav.__mega = true;
  const hoverOK = nav.dataset.hover !== 'false' && matchMedia('(hover:hover)').matches;
  let tOpen, tClose;
  const setOpen = (n, open) => {
    n.classList.toggle('open', open);
    const b = n.querySelector('.mtog'), m = n.querySelector('.mega');
    if (b) b.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (m) m.hidden = !open;
    if (!open) n.__pin = false;
  };
  const sync = () => { if (!nav.querySelector('.ni.open')) document.body.classList.remove('mega-open'); };
  const closeAll = (except) => { items.forEach((n) => { if (n !== except) setOpen(n, false); }); sync(); };
  const open = (n, pin) => { closeAll(n); setOpen(n, true); n.__pin = !!pin; document.body.classList.add('mega-open'); };
  const close = (n) => { setOpen(n, false); sync(); };
  const toggle = (n) => {
    clearTimeout(tOpen); clearTimeout(tClose);
    if (!n.classList.contains('open')) open(n, true);
    else if (n.__pin || !hoverOK) close(n);
    else n.__pin = true;
  };
  const plain = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  items.forEach((n) => {
    const a = n.querySelector(':scope > a'), b = n.querySelector('.mtog');
    if (hoverOK) {
      n.addEventListener('mouseenter', () => { clearTimeout(tClose); if (!n.classList.contains('open')) tOpen = setTimeout(() => open(n, false), 160); });
      n.addEventListener('mouseleave', () => { clearTimeout(tOpen); if (n.classList.contains('open') && !n.__pin) tClose = setTimeout(() => close(n), 260); });
    } else if (a && n.querySelector('.mega')) {
      a.addEventListener('click', (e) => { if (!plain(e)) return; e.preventDefault(); toggle(n); });
    }
    if (b) b.addEventListener('click', (e) => { e.preventDefault(); toggle(n); });
    /* keyboard: focus moving to something outside the item closes it; a click on something that takes no focus
       (relatedTarget null, as Safari does for buttons and links) is left to the outside-click handler */
    n.addEventListener('focusout', (e) => { if (e.relatedTarget && !n.contains(e.relatedTarget)) close(n); });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const o = nav.querySelector('.ni.open'); if (o) { close(o); const b = o.querySelector('.mtog'); if (b) b.focus(); }
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('.hdr .nav')) closeAll(); });
}

/* the drawer's Shop rows: the category name is a link, the chevron opens its sub-level; one open at a time,
   the current category's sub-level starts open */
function initDrawerSubs() {
  const drawer = $('[data-layer="nav"]'); if (!drawer) return;
  const btns = $$('.dx', drawer); if (!btns.length || drawer.__subs) return;
  drawer.__subs = true;
  const set = (b, open) => {
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
    const s = document.getElementById(b.getAttribute('aria-controls')); if (s) s.hidden = !open;
    const row = b.closest('.drow'); if (row) row.classList.toggle('exp', open);
  };
  btns.forEach((b) => b.addEventListener('click', (e) => {
    e.preventDefault();
    const on = b.getAttribute('aria-expanded') === 'true';
    btns.forEach((x) => set(x, false));
    if (!on) set(b, true);
  }));
  const cur = drawer.querySelector('.drow.on .dx'); if (cur) set(cur, true);
}

/* the header on phones: hides on scroll down, returns on scroll up; never while a drawer, sheet or panel is open */
function initHeaderHide() {
  const h = $('.hdr'); if (!h || h.__hide) return; h.__hide = true;
  let last = scrollY, acc = 0;
  /* ask-settle: the frame in which nocry-ask.js puts the page back where it was once its panel has closed; that jump is
     not a scroll down (28 October 2026) */
  const busy = /nav-open|cart-open|sheet-open|filters-open|ask-open|ask-settle|img-open|mega-open/;
  addEventListener('scroll', () => {
    const b = document.body;
    if (innerWidth >= 900 || busy.test(b.className)) { b.classList.remove('hdr-hide'); last = scrollY; return; }
    const y = scrollY, d = y - last; last = y;
    if (y < 96) { b.classList.remove('hdr-hide'); acc = 0; return; }
    acc = ((d > 0) === (acc > 0)) ? acc + d : d;
    if (acc > 28) b.classList.add('hdr-hide'); else if (acc < -14) b.classList.remove('hdr-hide');
  }, { passive: true });
}

function initLanguage() {
  $$('form[data-localization]').forEach((form) => {
    if (form.__loc) return; form.__loc = true;
    form.addEventListener('click', (e) => {
      const b = e.target.closest('[data-locale]'); if (!b) return;
      e.preventDefault();
      const input = form.querySelector('input[name="language_code"]'); if (input) input.value = b.dataset.locale;
      form.submit();
    });
  });
}

/* the sticky bar's height while it is up (0 while it is off: hidden, display none, or slid away with .off) */
function stickyH() { const st = $('.sticky'); return st && !st.classList.contains('off') && getComputedStyle(st).display !== 'none' ? st.offsetHeight : 0; }
function setVars() {
  setRootVar('--stickyh', stickyH() + 'px');
  const c = $('.consent');
  setRootVar('--consenth', c && c.classList.contains('show') ? c.offsetHeight + 'px' : '0px');
}

function initBuyVisible() {
  const buy = $('.buy, .lander-offer');
  if (!buy || !('IntersectionObserver' in window) || buy.__io) return;
  buy.__io = true;
  new IntersectionObserver((es) => es.forEach((e) => document.body.classList.toggle('buy-visible', e.isIntersecting)), { threshold: 0.05 }).observe(buy);
}

function initGuides() {
  if (window.innerWidth >= 900) $$('details.guide:not([data-stay-closed])').forEach((d) => { d.open = true; });
  const openGuide = () => { const g = $('#choose details.guide'); if (g && location.hash === '#choose') g.open = true; };
  openGuide(); addEventListener('hashchange', openGuide);
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href="#choose"], a[href$="#choose"]'); if (a) { const g = $('#choose details.guide'); if (g) g.open = true; }
    const s = e.target.closest('a[href="#specs"], a[href$="#specs"]'); if (s) { const d = $('#specs'); if (d) d.open = true; }
  });
}

/* card thumbnails: swipe (touch), drag (mouse), hover arrows, dots on desktop. Endless both ways: a copy of the
   last photo sits before the first and a copy of the first after the last, and landing on a copy jumps, without
   animation, to the real photo, so the arrows and a swipe keep going round. */
export function initCardGalleries(root = document) {
  $$('.card .cgal', root).forEach((g) => {
    if (g.__gal) return; g.__gal = true;
    const cg = g.querySelector('.cg'), dots = g.querySelector('.cdots');
    if (!cg) return;
    const slides = Array.from(cg.children), n = slides.length;
    cg.querySelectorAll('img').forEach((im) => { im.draggable = false; });
    if (n < 2) { g.querySelectorAll('.cnav, .cdots').forEach((b) => b.remove()); return; }
    const head = slides[n - 1].cloneNode(true), tail = slides[0].cloneNode(true);
    [head, tail].forEach((c) => { c.classList.add('cclone'); c.setAttribute('aria-hidden', 'true'); c.setAttribute('tabindex', '-1'); c.querySelectorAll('img').forEach((im) => { im.draggable = false; }); });
    cg.insertBefore(head, slides[0]); cg.appendChild(tail);
    const w = () => cg.clientWidth || 1;
    const idx = () => Math.round(cg.scrollLeft / w()); /* 0 = the copy of the last, 1..n = the photos, n + 1 = the copy of the first */
    const jump = (k) => { cg.style.scrollBehavior = 'auto'; cg.scrollLeft = k * w(); cg.style.scrollBehavior = ''; };
    const start = () => { if (cg.clientWidth && idx() === 0) jump(1); };
    start();
    if ('ResizeObserver' in window) new ResizeObserver(start).observe(cg); /* galleries that were hidden (a drawer) start when they get a size */
    if (dots) { dots.innerHTML = ''; for (let i = 0; i < n; i++) { const d = document.createElement('i'); if (i === 0) d.className = 'on'; dots.appendChild(d); } }
    const mark = () => { if (!dots) return; const k = ((idx() - 1) % n + n) % n; Array.from(dots.children).forEach((x, j) => { x.className = j === k ? 'on' : ''; }); };
    let settle = 0;
    cg.addEventListener('scroll', () => {
      mark(); clearTimeout(settle);
      settle = setTimeout(() => { const k = idx(); if (k === 0) jump(n); else if (k === n + 1) jump(1); }, 90);
    }, { passive: true });
    g.querySelectorAll('.cnav').forEach((bt) => bt.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      cg.scrollTo({ left: (idx() + (bt.classList.contains('next') ? 1 : -1)) * w(), behavior: scrollMode() });
    }));
    /* a mouse can drag the photos too (the theme editor's phone preview has no touch); a drag is not a click */
    let drag = null, dragged = false;
    cg.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button !== 0) return; drag = { x: e.clientX, left: cg.scrollLeft }; dragged = false; });
    cg.addEventListener('pointermove', (e) => {
      if (!drag) return; const dx = e.clientX - drag.x;
      if (!dragged && Math.abs(dx) < 5) return;
      if (!dragged) { dragged = true; cg.style.scrollSnapType = 'none'; }
      cg.scrollLeft = drag.left - dx;
    });
    const endDrag = () => {
      if (!drag) return; drag = null;
      if (!dragged) return;
      cg.style.scrollSnapType = '';
      cg.scrollTo({ left: Math.round(cg.scrollLeft / w()) * w(), behavior: scrollMode() });
    };
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) => cg.addEventListener(ev, endDrag));
    cg.addEventListener('click', (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);
  });
}

function initTrialLabel() {
  if (/[?&]v=trial(&|$)/.test(location.search)) $$('[data-label-trial]').forEach((x) => { x.textContent = x.dataset.labelTrial; });
}

function initHeroFill() {
  const fit = () => {
    const h = $('.hero.fill'); if (!h) return;
    const p = $('.proof');
    setRootVar('--proofh', (p ? p.offsetHeight : 0) + 'px');
    /* everything above the hero: announcement bar, header and, on phones, the link bar. Measured from the top of the
       body, not as the hero's place in the window plus the scroll: while Ask the Pack holds the page (nocry-ask.js
       fixes the body at minus the scroll, and the window then reads scroll 0) that sum came out short by the scroll,
       and a load or resize while the panel was open stretched the hero by as much (iPad, 28 October 2026) */
    setRootVar('--chrome', Math.round(h.getBoundingClientRect().top - document.body.getBoundingClientRect().top) + 'px');
  };
  fit(); addEventListener('resize', debounce(fit, 100)); addEventListener('load', fit);
}

function initViews() {
  $$('[data-view-event]').forEach((el) => {
    if (el.__viewed) return; el.__viewed = true;
    const kind = el.dataset.viewEvent, payload = el.dataset.viewPayload;
    if (kind === 'product' && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { dispatchView('product', payload, el); io.disconnect(); } }, { threshold: 0.2 });
      io.observe(el);
    } else dispatchView(kind, payload, el);
  });
}

/* the video lightbox: any element with data-video-src (mp4) or data-video-embed (YouTube/Vimeo) */
function initVideoBox() {
  const box = $('[data-videobox]'); if (!box || box.__init) return; box.__init = true;
  const stage = box.querySelector('[data-video-stage]'), title = box.querySelector('[data-video-title]');
  let opener = null;
  const embedUrl = (u) => {
    const yt = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/.exec(u);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`;
    const vm = /vimeo\.com\/(?:video\/)?(\d+)/.exec(u);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
    return u;
  };
  const open = (el) => {
    const src = el.dataset.videoSrc, emb = el.dataset.videoEmbed;
    title.textContent = el.dataset.videoTitle || '';
    stage.innerHTML = '';
    /* the box takes the clip's own shape (a vertical 9:16 clip opens tall, not as a strip in a 16:9 frame): from
       data-video-ratio when the tile carries it, else once the file's size is known; embeds stay 16:9 */
    box.style.removeProperty('--vr');
    const r = parseFloat(el.dataset.videoRatio);
    if (r > 0) box.style.setProperty('--vr', String(r));
    let media;
    if (src) {
      media = document.createElement('video');
      media.addEventListener('loadedmetadata', () => {
        if (media.videoWidth && media.videoHeight) box.style.setProperty('--vr', (media.videoWidth / media.videoHeight).toFixed(4));
      });
      media.src = src; media.controls = true; media.autoplay = true; media.playsInline = true;
    } else {
      media = document.createElement('iframe');
      media.src = embedUrl(emb); media.title = title.textContent;
      media.allow = 'autoplay; fullscreen; picture-in-picture'; media.allowFullscreen = true; media.loading = 'lazy';
    }
    stage.appendChild(media);
    opener = document.activeElement;
    box.classList.add('open'); focusInto(box, box.querySelector('[data-videoclose]'));
  };
  const close = () => {
    if (!box.classList.contains('open')) return;
    box.classList.remove('open'); stage.innerHTML = '';
    if (keyboardUser() && opener && opener.focus) opener.focus({ preventScroll: true });
    else if (box.contains(document.activeElement)) document.activeElement.blur();
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-video-src], [data-video-embed]');
    if (t && !t.closest('.gmain')) { e.preventDefault(); open(t); return; }
    if (e.target === box || e.target.closest('[data-videoclose]')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
    const t = e.target.closest && e.target.closest('[data-video-src], [data-video-embed]');
    if (t && (e.key === 'Enter' || e.key === ' ') && !t.closest('.gmain')) { e.preventDefault(); open(t); }
  });
  window.NOCRY = Object.assign(window.NOCRY || {}, { video: { open, close } });
}

/* legal pages: "On this page" from the body's H2s (ids added when missing) */
function initLegalToc() {
  $$('[data-legal]').forEach((root) => {
    const toc = root.querySelector('[data-toc]'), prose = root.querySelector('[data-prose]');
    if (!toc || !prose || toc.dataset.built) return;
    const hs = Array.from(prose.querySelectorAll('h2'));
    if (hs.length < 2) return;
    hs.forEach((h, i) => {
      if (!h.id) h.id = 'p' + (i + 1) + '-' + (h.textContent || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40);
      const a = document.createElement('a'); a.href = '#' + h.id;
      a.textContent = (h.textContent || '').replace(/^Section\s+(\d+):\s*/i, '$1. ');
      toc.appendChild(a);
    });
    toc.dataset.built = '1'; toc.hidden = false;
  });
}

/* "How to choose" and similar in-page links: hidden when the section they point to isn't on this page */
function initAnchorLinks() {
  $$('a.anchor-link[href^="#"]').forEach((a) => {
    const id = a.getAttribute('href').slice(1);
    if (!id) return;
    a.hidden = !document.getElementById(id);
  });
}

/* support links (text, WhatsApp, email) send one event per tap. "Start a chat" opens the chat app when it is
   loaded; when it is not, it scrolls to the form on the page (a person reads it), and on a page with no form it
   opens Ask the Pack. */
function initSupport() {
  document.addEventListener('click', (e) => {
    const page = document.body.dataset.page || location.pathname;
    const link = e.target.closest('[data-contact-channel]');
    if (link) track('contact_channel', { channel: link.dataset.contactChannel, page });
    const btn = e.target.closest('[data-chat-open]');
    if (!btn) return;
    e.preventDefault();
    track('contact_channel', { channel: 'chat', page });
    const chat = window.GorgiasChat;
    if (chat && typeof chat.open === 'function') { chat.open(); return; }
    const form = document.getElementById(btn.dataset.chatFallback || 'write');
    if (form) { form.scrollIntoView({ behavior: scrollMode() }); return; }
    const ask = window.NOCRY && window.NOCRY.ask;
    if (ask && typeof ask.open === 'function') ask.open();
  });
}

/* Looping background clips (the home hero, the lander hero, a photo slot with a clip): snippets/loop-video.liquid.
   They carry no autoplay attribute, so that Safari draws no play button on them in Low Power Mode (see the snippet),
   and the page plays them instead: a clip is started when it comes on screen and paused while it is off screen (the
   browser no longer does either by itself), started again after a return to the page (back button, another tab or app),
   and picked up when the theme editor redraws a section. A browser that refuses to play (Low Power Mode) leaves the clip
   on its first frame. Reduced motion: never started. A hero clip carries data-wait until the still beside it has
   loaded (snippets/loop-video.liquid starts it then, 27 October 2026): the watcher leaves it alone until that mark is gone. */
const LOOPS = 'video[loop][muted]:not([controls])';
const calm = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
let loopWatch = null;
function startLoop(v) {
  if (v.controls || !v.paused || document.hidden || (calm && calm.matches) || v.hasAttribute('data-wait')) return; /* controls: someone turned the sound on (initInplay); theirs now. data-wait: a hero clip the snippet starts once the still has loaded */
  v.muted = true;
  const p = v.play(); if (p && typeof p.catch === 'function') p.catch(() => {});
}
function initLoops(root = document) {
  const fresh = $$(LOOPS, root).filter((v) => !v.__loop);
  fresh.forEach((v) => { v.__loop = true; });
  if ('IntersectionObserver' in window) {
    if (!loopWatch) loopWatch = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) startLoop(e.target); else e.target.pause(); }));
    fresh.forEach((v) => loopWatch.observe(v));
  } else fresh.forEach(startLoop);
  if (window.__nocryLoops) return; window.__nocryLoops = true;
  const onScreen = (v) => { const r = v.getBoundingClientRect(); return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight; };
  const back = () => $$(LOOPS).forEach((v) => { if (onScreen(v)) startLoop(v); });
  addEventListener('pageshow', (e) => { if (e.persisted) back(); });
  document.addEventListener('visibilitychange', back);
}

/* A clip that plays in place (the product page's clip, [data-inplay]): the muted loop above is its preview; a tap plays it
   from the start with sound and the player's own controls, and from then on the loop watcher leaves it alone (startLoop
   skips a clip with controls) except to pause it off screen. is-on marks the muted preview while it plays (the play mark
   gives way to a sound-off mark); a clip the browser would not start (Low Power Mode, reduced motion) keeps its poster
   and play mark. */
function initInplay() {
  if (window.__nocryInplay) return; window.__nocryInplay = true;
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-inplay-sound]'); if (!b) return;
    const box = b.closest('[data-inplay]'), v = box && box.querySelector('video'); if (!v) return;
    e.preventDefault();
    v.loop = false; v.muted = false; v.controls = true;
    try { v.currentTime = 0; } catch (err) { /* not loaded yet: it starts at 0 anyway */ }
    box.classList.add('is-sound'); box.classList.remove('is-on');
    const p = v.play(); if (p && typeof p.catch === 'function') p.catch(() => {});
    v.focus({ preventScroll: true });
  });
  const mark = (e) => {
    const box = e.target && e.target.closest && e.target.closest('[data-inplay]'); if (!box || box.classList.contains('is-sound')) return;
    box.classList.toggle('is-on', e.type === 'playing');
  };
  document.addEventListener('playing', mark, true);
  document.addEventListener('pause', mark, true);
}

function initAll(root) {
  initNav(); initLanguage(); initBuyVisible(); initCardGalleries(root); initViews(); initVideoBox(); initLegalToc(); initAnchorLinks(); initLoops(root);
  setVars();
}

onReady(() => {
  initAll(document);
  initGuides(); initTrialLabel(); initHeroFill(); initHeaderHide(); initSupport(); initInplay();
  addEventListener('resize', debounce(setVars, 100));
  document.addEventListener('nocry:consent:shown', setVars);
  /* --stickyh used to be read once, at load, when a bar is still hidden: the wolf then sat at the foot of the screen, over the
     bar's button, once the bar came up. The bars are watched now, so the wolf and the toast follow them (28 October 2026) */
  if ('MutationObserver' in window) $$('.sticky').forEach((el) => new MutationObserver(debounce(setVars, 50)).observe(el, { attributes: true, attributeFilter: ['style', 'hidden', 'class'] }));
  document.addEventListener('nocry:section:rendered', (e) => initAll(e.detail && e.detail.root || document));
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initAll(e.target));
    const layerOf = (section) => section.querySelector('[data-layer]') || (section.querySelector('.hdr') ? $('[data-layer="nav"]') : null);
    document.addEventListener('shopify:section:select', (e) => {
      const panel = layerOf(e.target);
      if (panel) layers.open(panel.dataset.layer, panel);
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      const panel = layerOf(e.target);
      if (panel) layers.close(panel.dataset.layer);
    });
  }
});
